import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useSubscriptionActions} from "../../service/useSubscriptionActions";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import SectionHeader from "../../../../util/component/SectionHeader";
import {useTranslation} from "react-i18next";
import {
    boolToString,
    stringToBool,
    formatToFloat,
    floatToIntAmount,
    formatDate,
    adaptDateTimeForDateTimePicker
} from "../../component/Utils";
import {
    Autocomplete,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Tooltip,
    useTheme
} from "@mui/material";
import {Controller, useForm} from "react-hook-form";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import {ID_DISMISSED, ID_DRAFT, ID_READY} from "./constants/SubscriptionsStatuses";
import {useSubscriptionColors} from "./constants/useSubscriptionColors";
import {CheckCircleRounded, InfoRounded} from "@mui/icons-material";
import AdvancedDmsSelector from "../../component/AdvancedDmsSelector/AdvancedDmsSelector";
import {DateTimePicker} from "@mui/x-date-pickers";
import {SubscriptionDetail} from "./constants/SubscriptionModels";
import {useLookupActions} from "../../service/useLookupActions";
import CustomModal from "../../component/CustomModal";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {CURRENCY_REG_EXP, INTEGER_REG_EXP} from "../../component/Constants";
import {
    preselectSingleEntity,
    UserLegalEntitiesOrDealersInputGroup
} from "../../component/UserLegalEntitiesOrDealersInputGroup/UserLegalEntitiesOrDealersInputGroup";
import useCategoriesFlexRent from "../../service/useCategoriesFlexRent";
import AcrissMonthlyFeeTable from "./components/AcrissMonthlyFeeTable";


const SubscriptionCreateOrUpdatePage = () => {
    const theme = useTheme();
    const {t} = useTranslation(['salesmarketing'], {keyPrefix: 'subscription'});
    const location: any = useLocation();
    const {subscriptionId} = useParams();
    const isUpdate = location.pathname.includes('/salesmarketing/subscriptionupdate/') && !!subscriptionId;
    const copyId = location.state && location.state.copy ? location.state.copy : '';
    const [subscription, setSubscription] = useState<SubscriptionDetail>();
    const [categories, setCategories] = useState<any>([]);
    const [products, setProducts] = useState<any>([]);
    const [dmsList, setDmsList] = useState<any>([]);
    const [openCreationModal, setOpenCreationModal] = useState<boolean>(false);
    const navigate = useNavigate();
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const [acrissList, setAcrissList] = useState<string[]>([]);
    const mappedCategories = useCategoriesFlexRent(categories);
    const [acrisExt, setAcrisExt] = useState({} as Record<string, number>)
    const [name, setName] = useState('');
    const [idPlan, setIdPlan] = useState('');
    const {getSubscriptionColors} = useSubscriptionColors();
    const [editFields, setEditFields] = useState<boolean>(false);
    const {
        getSubscriptionById,
        createSubscription,
        updateSubscription,
        getCategoriesBySegmentCode,
        getProductCodes,
    } = useSubscriptionActions();

    const {getRatePlanInfoByProductCode} = useLookupActions();

    useEffect(() => {
        getCategoriesBySegmentCode().then((res: any) => {
            setCategories(res);
        });
        if (!isUpdate) {
            getProductCodes().then((pc: any) => {
                setProducts(pc.result.content);
                if (copyId !== '') {
                    getSubscriptionById(copyId).then((sub: any) => {
                        let p = pc.result.content.find((p: any) => p.productCode === sub.productCode);
                        setSubscription({
                            ...sub,
                            name: sub.name + (' - Copy'),
                            productName: p.name,
                            productDescription: p.description,
                            productStartDate: p.startDate,
                            productEndDate: p.endDate,
                            id: ''
                        });
                    });
                }
            });
        }
    }, []);

    useEffect(() => {
        const fetchForUpdate = async () => {
            if (isUpdate && subscriptionId) {
                let sub = await getSubscriptionById(subscriptionId);
                let prods = await getProductCodes(categories.find((c: any) => c.categoryName === sub.category)?.categoryCode);
                setProducts(prods.result.content);
                let p = prods.result.content.find((p: any) => p.productCode === sub.productCode);
                getRatePlanInfoByProductCode(p.productCode).then((res: any) => {
                    setAcrisExt(res.acrisExt)
                })
                setAcrissList(sub.acrissCodeList || []);
                setSubscription({
                    ...sub,
                    productName: p.name,
                    productDescription: p.description,
                    productStartDate: p.startDate,
                    productEndDate: p.endDate
                });
                setEditFields(new Date() > new Date(sub.activableStartDate));
            }
        };
        if (categories.length > 0 && isUpdate) {
            fetchForUpdate();
        }
        return () => {
        };
    }, [categories]);


    const statuses = [
        {
            value: ID_DRAFT,
            label: t("statuses." + ID_DRAFT),
            color: getSubscriptionColors(ID_DRAFT)
        },
        {
            value: ID_READY,
            label: t("statuses." + ID_READY),
            color: getSubscriptionColors(ID_READY)
        },
        {
            value: ID_DISMISSED,
            label: t("statuses." + ID_DISMISSED),
            color: getSubscriptionColors(ID_DISMISSED)
        }
    ];

    const retrieveInitialValues = () => {
        if ((isUpdate || copyId !== '') && subscription) {
            return {
                tenantId: subscription.tenantId ? subscription.tenantId : "",
                dealerId: subscription.dealerId ? subscription.dealerId : "",
                status: subscription.status ? subscription.status : "",
                category: subscription.category ? subscription.category : "",
                name: subscription.name ? subscription.name : "",
                priority: subscription.priority ? subscription.priority : "null",
                sku: subscription.sku ? subscription.sku : "",
                pickupDms: subscription.pickupDms ? subscription.pickupDms : [],
                activableStartDate: subscription?.activableStartDate ? adaptDateTimeForDateTimePicker(subscription.activableStartDate) : null,
                activableEndDate: subscription.activableEndDate ? adaptDateTimeForDateTimePicker(subscription.activableEndDate) : null,
                entryFee: subscription.entryFee ? formatToFloat(subscription.entryFee, 0.01) : "0.00",
                visible: subscription.visible ? stringToBool(subscription.visible) : false,
                purchasable: subscription.purchasable ? stringToBool(subscription.purchasable) : false,
                activable: subscription.activable ? stringToBool(subscription.activable) : false,
                activableVoucherType: subscription.activableVoucherType ? stringToBool(subscription.activableVoucherType) : false,
                totalRate: subscription.totalRate ? subscription.totalRate : "",
                flexRentType: subscription.flexRentType ? subscription.flexRentType : "",
                productCode: subscription.productCode ? subscription.productCode : '',
                productName: subscription.productName ? subscription.productName : '',
                productDescription: subscription.productDescription ? subscription.productDescription : '',
                productStartDate: subscription?.productStartDate ? adaptDateTimeForDateTimePicker(subscription.productStartDate) : null,
                productEndDate: subscription?.productEndDate ? adaptDateTimeForDateTimePicker(subscription.productEndDate) : null,
                switchable: subscription.switchable ? stringToBool(subscription.switchable) : false,
                startRenewalDay: subscription.startRenewalDay ? subscription.startRenewalDay : null,
                endRenewalDay: subscription.endRenewalDay ? subscription.endRenewalDay : null,
                annualRenewalDiscount: subscription.annualRenewalDiscount !== null ? subscription.annualRenewalDiscount : null,
                renewable: subscription.renewable ? stringToBool(subscription.renewable) : false,
                mobilityWallet: subscription.mobilityWallet ? stringToBool(subscription.mobilityWallet) : false,
                walletIncludedDays: subscription.walletIncludedDays ? subscription.walletIncludedDays : '',
                walletCalendarDays: subscription.walletCalendarDays ? subscription.walletCalendarDays : '',
                mobilityVoucher: subscription.mobilityVoucher ? stringToBool(subscription.mobilityVoucher) : false,
                acrisExt: subscription.acrisExt ? subscription.acrisExt : null,
                createdDate: subscription.createdDate ? subscription.createdDate : null,
                lastModifiedDate: subscription.lastModifiedDate ? subscription.lastModifiedDate : null

            };
        } else {
            return {
                tenantId: "",
                dealerId: "",
                status: "",
                category: "",
                name: "",
                priority: "",
                sku: "",
                pickupDms: [],
                activableStartDate: null,
                activableEndDate: null,
                entryFee: "",
                purchasable: false,
                activable: false,
                activableVoucherType: false,
                visible: false,
                totalRate: "",
                flexRentType: "",
                productCode: '',
                productName: '',
                productDescription: '',
                productStartDate: null,
                productEndDate: null,
                switchable: false,
                startRenewalDay: null,
                endRenewalDay: null,
                annualRenewalDiscount: null,
                renewable: false,
                mobilityWallet: false,
                walletIncludedDays: '',
                walletCalendarDays: '',
                mobilityVoucher: false,
                acrisExt: null
            };
        }
    }

    const validationSchema: any = Yup.object({
        tenantId: Yup.string().nullable().test('testTenant', t('form.requiredCheck'), (value) => {
            if (globalData?.user?.legalEntities?.length === 1)
                return true;
            return !!value;
        }),
        dealerId: Yup.string().nullable().test('testDealer', t('form.requiredCheck'), (value) => {
            if (globalData?.user?.dealers?.length === 1)
                return true;
            return !!value;
        }),
        status: Yup.string().required(t('form.requiredCheck')),
        category: Yup.string().required(t('form.requiredCheck')),
        name: Yup.string().required(t('f.requiredCheck')),
        priority: Yup.number().min(1).max(100).required(t('form.requiredCheck')),
        sku: Yup.string().required(t('form.requiredCheck')),
        pickupDms: Yup.array().required(t('form.requiredCheck')).min(1, t('form.requiredCheck')),
        activableStartDate: Yup.date().nullable().required(t('form.requiredCheck')).typeError(t('form.invalidDate'))
            .test("testStartDate", t('form.datesCheck'), (value, context) => {
                return (value && context.parent.activableEndDate && value <= context.parent.activableEndDate)
            })
            .test("validStartDate", t('form.validStartDate'), (value) => {
                let productStartDate: Date | null = watch('productStartDate');
                if (!!value && !!productStartDate)
                    return value >= new Date(productStartDate);
                return true
            }),
        activableEndDate: Yup.date().nullable().required(t('form.requiredCheck')).typeError(t('form.invalidDate'))
            .test("testEndDate", t('form.datesCheck'), (value, context) => {
                return (value && context.parent.activableStartDate && value >= context.parent.activableStartDate)
            })
            .test("validEndDate", t('form.validEndDate'), (value) => {
                let productEndDate: Date | null = watch('productEndDate');
                if (!!value && !!productEndDate)
                    return value <= new Date(productEndDate);
                return true
            })
        ,
        entryFee: Yup.string().required(t('form.requiredCheck')).test('currency', t('form.currencyCheck'), (value) => {
            return (!!value && value.toString().match(CURRENCY_REG_EXP) !== null);
        }),
        annualRenewalDiscount: Yup.number().nullable().min(0).max(100),
        startRenewalDay: Yup.number().nullable().min(1).max(30)
            .test("testRenewal", t("form.renewalCheck"), (value, context) => {
                if (context.parent.category === 'CarCloud' || context.parent.category === 'BE FREE EVO')
                    return (value && context.parent.endRenewalDay && value <= context.parent.endRenewalDay)
                return true;
            }),
        endRenewalDay: Yup.number().nullable().min(1).max(30)
            .test("testRenewal", t("form.renewalCheck"), (value, context) => {
                if (context.parent.category === 'CarCloud' || context.parent.category === 'BE FREE EVO')
                    return (value && context.parent.startRenewalDay && value >= context.parent.startRenewalDay)
                return true;
            }),
        walletIncludedDays: Yup.string().when('mobilityWallet', {
            is: true,
            then: Yup.string().required(t('form.requiredCheck')).matches(INTEGER_REG_EXP, t('walletTest')).test('testWallet', t('walletIncludedTest'), (value, context) => {
                return !!value && context.parent.walletCalendarDays && parseInt(value) <= parseInt(context.parent.walletCalendarDays);
            }),
            otherwise: Yup.string().nullable()
        }),
        walletCalendarDays: Yup.string().when('mobilityWallet', {
            is: true,
            then: Yup.string().required(t('form.requiredCheck')).matches(INTEGER_REG_EXP, t('walletTest')).test('testWallet', t('walletTest'), (value, context) => {
                return !!value && parseInt(value) >= 30 && parseInt(value) <= 360 && context.parent.walletIncludedDays && parseInt(context.parent.walletIncludedDays) <= parseInt(value)
            }).test('multiple', t('walletTestMultiple'), (value) => {
                return !!value && parseInt(value) % 30 === 0;
            }),
            otherwise: Yup.string().nullable()
        }),
        monthlyFee: Yup.string().nullable(),
        totalRate: Yup.string().nullable(),
        flexRentType: Yup.string().when('category', {
            is: 'Flex Rent',
            then: Yup.string().required(t('form.requiredCheck')),
            otherwise: Yup.string().nullable(),
        }),

    })

    const {
        handleSubmit,
        control,
        setValue,
        getValues,
        watch,
        resetField,
        reset
    } = useForm({
        defaultValues: retrieveInitialValues(),
        resolver: yupResolver(validationSchema)
    });


    const submit = (data: any) => {

        if (data.monthlyFee) {
            delete data.monthlyFee
        }

        data = {
            ...data,
            activable: boolToString(data.activable),
            activableVoucherType: boolToString(data.activableVoucherType),
            purchasable: boolToString(data.purchasable),
            switchable: boolToString(data.switchable),
            renewable: boolToString(data.renewable),
            visible: boolToString(data.visible),
            mobilityWallet: boolToString(data.mobilityWallet),
            mobilityVoucher: boolToString(data.mobilityVoucher),
            entryFee: floatToIntAmount(data.entryFee),
            acrisExt
        };


        //don't need to pass them
        delete data.productName;
        delete data.productDescription;
        delete data.productStartDate;
        delete data.productEndDate;

        if (isUpdate) {
            data = {...data, id: subscriptionId};
            updateSubscription(data).then((res: any) => {
                if (res.status === 'OK') {
                    setName(data.name)
                    setIdPlan(res.result.id)
                    setOpenCreationModal(true);
                } else {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t("form.updateError"),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                }
            });
        } else {
            if (globalData.user.legalEntities.length === 1) {
                data = {...data, tenantId: globalData.user.legalEntities[0].name}
            }

            if (globalData.user.dealers.length === 1) {
                data = {...data, dealerId: globalData.user.dealers[0].name}
            }

            createSubscription(data).then((res: any) => {
                if (res.status === 'OK') {
                    setName(data.name)
                    setIdPlan(res.result.id)
                    setOpenCreationModal(true);
                } else {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t("form.creationError"),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                }
            });
        }
    }

    useEffect(() => {
        if (watch('productName') && watch('productName') !== '' && products) {
            let product = products.find((p: any) => p.name === watch('productName'));

            if (product) {
                setValue('category', categories.find((c: any) => c.categoryCode === product.categoryCode).categoryName || '');
                setValue('productCode', product.productCode);
                setValue('productDescription', product.description);
                setValue('productStartDate', product.startDate);
                setValue('productEndDate', product.endDate);
                getRatePlanInfoByProductCode(product.productCode).then((p: any) => {
                    setDmsList(p.locationsExt);
                    setAcrissList(p.carGroups)
                });
            }
        }

    }, [watch('productName')]);

    useEffect(() => {
        if (!isUpdate) {
            setValue('productName', '')
            setValue('category', '')
            setValue('productCode', '');
            setValue('productDescription','');
            setValue('productStartDate', null);
            setValue('productEndDate', null);
        }
    }, [watch('tenantId'), watch('dealerId')]);

    useEffect(() => {
        reset(retrieveInitialValues())
    }, [subscription]);

    useEffect(() => {
        if (!isUpdate) {
            if (watch('mobilityWallet')) {
                setValue("walletIncludedDays", "");
                setValue("walletCalendarDays", "");
            } else {
                setValue("walletIncludedDays", "240");
                setValue("walletCalendarDays", "360");
            }
        }
    }, [watch('mobilityWallet')])

    const getTitle = (category: string) => {
        return (
            <Stack spacing={2}>
                <div><b>{t('form.infoBox.titles.purchasable')}</b>{t('form.infoBox.contents.purchasable')}</div>
                <div><b>{t('form.infoBox.titles.mobilityVoucher')}</b>{t('form.infoBox.contents.mobilityVoucher')}</div>
                <div><b>{t('form.infoBox.titles.visible')}</b>{t('form.infoBox.contents.visible')}</div>
                <div><b>{t('form.infoBox.titles.activable')}</b>{t('form.infoBox.contents.activable')}</div>
                {(category === 'CarCloud' || category === 'BE FREE EVO') &&
                    <div><b>{t('form.infoBox.titles.renewable')}</b>{t('form.infoBox.contents.renewable')}</div>
                }
                {category === 'CarCloud' &&
                    <div><b>{t('form.infoBox.titles.switchable')}</b>{t('form.infoBox.contents.switchable')}</div>
                }
                <div>
                    <b>{t('form.infoBox.titles.activableVoucherType')}</b>{t('form.infoBox.contents.activableVoucherType')}
                </div>
            </Stack>
        )
    }

    useEffect(() => {
        preselectSingleEntity(globalData, setValue);
    }, [globalData, setValue]);

    return (
        <>
            <SectionHeader
                title={t(isUpdate ? 'updateHeader.title' : 'createHeader.title')}
                subtitle={t(isUpdate ? 'updateHeader.subTitle' : 'createHeader.subTitle')}/>
            <Paper elevation={3} sx={{padding: "16px", margin: "18px"}}>
                <form onSubmit={handleSubmit(data => {
                    submit({...data});
                }, errors => console.log(errors))}>
                    <Grid container rowSpacing={3} columnSpacing={3}>
                        <UserLegalEntitiesOrDealersInputGroup control={control} disabled={isUpdate}/>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"status"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        required
                                        name="status"
                                        label={t('form.status')}
                                        select
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    >
                                        {statuses?.map((a) => (
                                            <MenuItem key={a.value} value={a.value}>
                                                {a.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"name"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        required
                                        name="name"
                                        label={t('form.name')}
                                        disabled={editFields}
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                fullWidth
                                disabled
                                InputProps={{
                                    endAdornment: <Tooltip arrow title={t('form.idTooltip')}><InfoRounded
                                        color="primary"/></Tooltip>
                                }}
                                label={t('form.id')}
                                value={subscription && subscription.id ? subscription.id : ''}
                                placeholder={t('form.idPlaceholder')}
                            />
                        </Grid>
                        <Grid item xs={3}/>
                        <Grid item xs={3}>
                            {categories && <Controller
                                control={control}
                                name={"category"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        required
                                        disabled={isUpdate}
                                        name="category"
                                        label={t('form.category')}
                                        select
                                        value={field.value}
                                        onChange={(e) => {
                                            resetField('productName');
                                            resetField('productCode');
                                            resetField('productDescription');
                                            resetField('productStartDate');
                                            resetField('productEndDate');
                                            resetField('pickupDms')
                                            setValue('category', e.target.value);
                                            const currentSelectedCategoryCode = categories.find((category: any) => category.categoryName === e.target.value).categoryCode
                                            setValue('totalRate', mappedCategories.get(currentSelectedCategoryCode) ?? '')
                                        }}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    >
                                        {categories?.map((c: any) => (
                                            <MenuItem key={c.categoryCode} value={c.categoryName}>
                                                {c.categoryName}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}/>
                            }
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"productName"}
                                render={({field, fieldState}) => (
                                    <Autocomplete
                                        id={'productName'}
                                        value={field.value}
                                        disabled={editFields || watch('tenantId') === '' || watch('dealerId') === ''}
                                        onChange={(event: any, newValue: any | null, reason) => {
                                            if (reason === "clear") {
                                                resetField('category');
                                                resetField('productCode');
                                                resetField('productStartDate');
                                                resetField('productEndDate');
                                                resetField('pickupDms');
                                                setAcrissList([]);
                                            }
                                            setValue('productName', newValue);
                                        }}
                                        noOptionsText={watch('category') !== '' ? t('form.noOptions') : undefined}
                                        options={products.filter((t:any) => t.tenantId === watch('tenantId')  && t.dealerId === watch('dealerId'))
                                            .filter((p: any) => {
                                                if (watch('category') !== '') {
                                                    return p.categoryCode === categories.find((c: any) => c.categoryName === watch('category')).categoryCode
                                                }
                                                return true;
                                            }).map((p: any) => p.name) || []}
                                        renderInput={(params) =>
                                            <TextField
                                                required
                                                {...params} label={t('form.productName')}/>
                                        }></Autocomplete>
                                )}/>
                        </Grid>

                        {watch('category') === "Flex Rent" && <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"flexRentType"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        disabled={isUpdate}
                                        fullWidth
                                        select
                                        required
                                        label={t('form.flexRentType')}
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        type="number"
                                    >
                                        {['30', '90']?.map((a) => (
                                            <MenuItem key={a} value={a}>
                                                {a}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}/>
                        </Grid>}
                        <Grid item xs={3}/>
                        {watch('category') !== "Flex Rent" && <Grid item xs={3}/>}
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"productCode"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        label={t('form.productCode')}
                                        value={field.value}
                                        disabled
                                        inputProps={{
                                            readOnly: true
                                        }}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"productDescription"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        label={t('form.productDescription')}
                                        value={field.value}
                                        disabled
                                        inputProps={{
                                            readOnly: true
                                        }}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="productStartDate"
                                render={({field, fieldState}) => (
                                    <DateTimePicker
                                        ampm={false}
                                        label={t('form.productStartDate')}
                                        value={field.value}
                                        onChange={field.onChange}
                                        disabled
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                fullWidth
                                                //inputProps={{readOnly: true}}
                                            />}
                                        inputFormat={"L HH:mm"}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name="productEndDate"
                                render={({field, fieldState}) => (
                                    <DateTimePicker
                                        ampm={false}
                                        disabled
                                        label={t('form.productEndDate')}
                                        value={field.value}
                                        onChange={field.onChange}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                fullWidth
                                                //inputProps={{readOnly: true}}
                                            />}
                                        inputFormat={"L HH:mm"}
                                    />
                                )}/>
                        </Grid>

                        <Grid item container xs={9} rowSpacing={3} columnSpacing={3}>
                            <Grid item xs={4}>
                                <Controller
                                    control={control}
                                    name={"priority"}
                                    render={({field, fieldState}) => (
                                        <TextField
                                            fullWidth
                                            label={t('form.priority')}
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            required
                                            type="number"
                                        />
                                    )}/>
                            </Grid>
                            <Grid item xs={4}>
                                <Controller
                                    control={control}
                                    name={"sku"}
                                    render={({field, fieldState}) => (
                                        <TextField
                                            fullWidth
                                        required
                                            name="sku"
                                            label={t('form.sku')}
                                            value={field.value}
                                            disabled={editFields}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}/>
                            </Grid>
                            <Grid item xs={4}>
                                <Controller
                                    control={control}
                                    name={"pickupDms"}
                                    render={({field, fieldState: {error}}) => (
                                        <AdvancedDmsSelector key={dmsList} error={error} required={true}
                                                             disabled={dmsList.length === 0 || watch('productCode') === ''}
                                                             value={field.value}
                                                             setValue={setValue} fieldName={"pickupDms"} items={dmsList}
                                                             label={t('form.dms')} tenantId={watch('tenantId')}/>
                                    )}/>
                            </Grid>
                            <Grid item xs={4}>
                                <Controller
                                    control={control}
                                    name="activableStartDate"
                                    render={({field, fieldState}) => (
                                        <DateTimePicker
                                            ampm={false}
                                            label={t('form.startDate')}
                                            value={field.value}
                                            disabled={editFields}
                                            onChange={field.onChange}
                                            minDate={watch('productStartDate') ? watch('productStartDate') : null}
                                            maxDate={watch("activableEndDate") ? watch("activableEndDate") : null}
                                            renderInput={(params) =>
                                                <TextField
                                                    {...params}
                                                    required
                                                    fullWidth
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                />}
                                            inputFormat={"DD/MM/YYYY HH:mm"}
                                        />
                                    )}/>
                            </Grid>
                            <Grid item xs={4}>
                                <Controller
                                    control={control}
                                    name="activableEndDate"
                                    render={({field, fieldState}) => (
                                        <DateTimePicker
                                            ampm={false}
                                            label={t('form.endDate')}
                                            value={field.value}
                                            onChange={field.onChange}
                                            minDate={watch("activableStartDate") ? watch("activableStartDate") : null}
                                            maxDate={watch('productEndDate') ? watch('productEndDate') : null}
                                            renderInput={(params) =>
                                                <TextField
                                                    {...params}
                                                    required
                                                    fullWidth
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                />}
                                            inputFormat={"L HH:mm"}
                                        />
                                    )}/>
                            </Grid>
                            <Grid item xs={4}>
                                <Controller
                                    control={control}
                                    name={"entryFee"}
                                    render={({field, fieldState}) => (
                                        <TextField
                                            fullWidth
                                            required
                                            name="entryFee"
                                            label={t('form.entryFee')}
                                            value={field.value}
                                            onBlur={() => {
                                                if (!watch('entryFee')?.includes('.') && !watch('entryFee')?.includes(',')) {
                                                    setValue('entryFee', watch('entryFee') + '.00');
                                                }
                                            }}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}/>
                            </Grid>
                            {isUpdate &&
                                <>
                                    <Grid item xs={4}>
                                        <TextField
                                            fullWidth
                                            label={t('form.createdDate')}
                                            value={subscription?.createdDate ? formatDate(subscription.createdDate) : '--'}
                                            margin="dense"
                                            variant="standard"
                                            inputProps={
                                                {readOnly: true}
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <TextField
                                            fullWidth
                                            label={t('form.lastModifiedDate')}
                                            value={subscription?.lastModifiedDate ? formatDate(subscription.lastModifiedDate) : '--'}
                                            margin="dense"
                                            variant="standard"
                                            inputProps={
                                                {readOnly: true}
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={4}></Grid>
                                </>
                            }
                            {(watch('category') === 'CarCloud' || watch('category') === 'BE FREE EVO') &&
                                <>
                                    <Grid item xs={4}>
                                        <Controller
                                            control={control}
                                            name={"startRenewalDay"}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    fullWidth
                                                    required
                                                    label={t('form.startRenewalDay')}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                />
                                            )}/>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Controller
                                            control={control}
                                            name={"endRenewalDay"}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    fullWidth
                                                    required
                                                    label={t('form.endRenewalDay')}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                />
                                            )}/>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Controller
                                            control={control}
                                            name={"annualRenewalDiscount"}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    fullWidth
                                                    required
                                                    label={t('form.discount')}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                    InputProps={{
                                                        startAdornment: <InputAdornment
                                                            position="start">%</InputAdornment>,
                                                    }}
                                                />
                                            )}/>
                                    </Grid>
                                </>
                            }
                            <Grid item xs={4}>
                                <Controller
                                    control={control}
                                    name={"purchasable"}
                                    render={({field, fieldState}) => (
                                        <FormControlLabel
                                            label={t('form.purchasable')}
                                            control={<Checkbox
                                                color={"accent"}
                                                sx={{color: theme.palette.accent.main}}
                                                checked={field.value}
                                                onChange={field.onChange}
                                            />}
                                        />
                                    )}/>
                            </Grid>
                            <Grid item xs={4}>
                                <Controller
                                    control={control}
                                    name={"mobilityVoucher"}
                                    render={({field, fieldState}) => (
                                        <FormControlLabel
                                            label={t('form.mobilityVoucher')}
                                            control={<Checkbox
                                                color={"accent"}
                                                sx={{color: theme.palette.accent.main}}
                                                checked={field.value}
                                                onChange={field.onChange}
                                            />}
                                        />
                                    )}/>
                            </Grid>
                            <Grid item xs={4}>
                                <Controller
                                    control={control}
                                    name={"visible"}
                                    render={({field, fieldState}) => (
                                        <FormControlLabel
                                            label={t('form.visible')}
                                            control={<Checkbox
                                                color={"accent"}
                                                sx={{color: theme.palette.accent.main}}
                                                checked={field.value}
                                                onChange={field.onChange}
                                            />}
                                        />
                                    )}/>
                            </Grid>
                            <Grid item xs={4}>
                                <Controller
                                    control={control}
                                    name={"activable"}
                                    render={({field, fieldState}) => (
                                        <FormControlLabel
                                            label={t('form.activable')}
                                            control={<Checkbox
                                                color={"accent"}
                                                sx={{color: theme.palette.accent.main}}
                                                checked={field.value}
                                                onChange={field.onChange}
                                            />}
                                        />
                                    )}/>
                            </Grid>
                            <Grid item xs={4}>
                                <Controller
                                    control={control}
                                    name={"activableVoucherType"}
                                    render={({field, fieldState}) => (
                                        <FormControlLabel
                                            label={t('form.activableVoucherType')}
                                            control={<Checkbox
                                                color={"accent"}
                                                sx={{color: theme.palette.accent.main}}
                                                checked={field.value}
                                                onChange={field.onChange}
                                            />}
                                        />
                                    )}/>
                            </Grid>
                            <Grid item xs={4} display={'flex'} justifyContent={'start'} alignItems={'center'}>
                                <Tooltip arrow title={getTitle(watch('category'))}>
                                    <InfoRounded color="primary"/>
                                </Tooltip>
                            </Grid>
                            {watch('category') === 'CarCloud' &&
                                <Grid item xs={4}>
                                    <Controller
                                        control={control}
                                        name={"switchable"}
                                        render={({field, fieldState}) => (
                                            <FormControlLabel
                                                label={t('form.switchable')}
                                                control={<Checkbox
                                                    color={"accent"}
                                                    sx={{color: theme.palette.accent.main}}
                                                    checked={field.value}
                                                    onChange={field.onChange}
                                                />}
                                            />
                                        )}/>
                                </Grid>
                            }
                            {(watch('category') === 'CarCloud' || watch('category') === 'BE FREE EVO') &&
                                <Grid item xs={4}>
                                    <Controller
                                        control={control}
                                        name={"renewable"}
                                        render={({field, fieldState}) => (
                                            <FormControlLabel
                                                label={t('form.renewable')}
                                                control={<Checkbox
                                                    color={"accent"}
                                                    sx={{color: theme.palette.accent.main}}
                                                    checked={field.value}
                                                    onChange={field.onChange}
                                                />}
                                            />
                                        )}/>
                                </Grid>
                            }
                            {(watch('category') === 'Car Box') &&
                                <>
                                    <Grid item xs={4}>
                                        <Controller
                                            control={control}
                                            name={"mobilityWallet"}
                                            render={({field, fieldState}) => (
                                                <FormControlLabel
                                                    sx={{mt: 3}}
                                                    label={t('form.mobilityWallet')}
                                                    control={<Checkbox
                                                        color={"accent"}
                                                        sx={{color: theme.palette.accent.main}}
                                                        checked={field.value}
                                                        onChange={event => {
                                                            field.onChange(event);
                                                            if (event.target.checked) {
                                                                setValue("walletIncludedDays", "");
                                                                setValue("walletCalendarDays", "");
                                                            } else {
                                                                setValue("walletIncludedDays", "240");
                                                                setValue("walletCalendarDays", "360");
                                                            }
                                                        }}
                                                    />}
                                                />
                                            )}/>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Controller
                                            control={control}
                                            name={"walletIncludedDays"}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    fullWidth
                                                    required
                                                    type={'number'}
                                                    label={t('form.walletIncludedDays')}
                                                    value={field.value}
                                                    disabled={!watch('mobilityWallet')}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                />
                                            )}/>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Controller
                                            control={control}
                                            name={"walletCalendarDays"}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    fullWidth
                                                    required
                                                    type={'number'}
                                                    label={t('form.walletCalendarDays')}
                                                    disabled={!watch('mobilityWallet')}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                />
                                            )}/>
                                    </Grid>
                                    <Grid item xs={4}/>
                                </>
                            }
                        </Grid>

                        <Grid item container xs={3}>
                            {Object.keys(acrisExt).length > 0 &&
                                <Grid item xs={12} sx={{overflowY: 'auto', maxHeight: '250px'}}>
                                    <AcrissMonthlyFeeTable data={acrisExt} t={t}/>
                                </Grid>
                            }
                        </Grid>

                    </Grid>
                    <Grid container rowSpacing={3} columnSpacing={3} sx={{paddingTop: "50px"}}>
                        <Grid container item xs={12} rowSpacing={1} columnSpacing={3} justifyContent={"flex-end"}>
                            <Grid item>
                                <Button variant={"outlined"} color={"accent"}
                                        onClick={() => {
                                            navigate("/salesmarketing/subscriptionlist")
                                        }}>
                                    {t('form.buttons.cancel')}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button color={"accent"}
                                        type="submit">{t('form.buttons.confirm')}</Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
            <CustomModal title={isUpdate ? t('form.modal.updateSuccess') : t('form.modal.createSuccess')}
                         content={() => <p>{isUpdate ? t('form.modal.updateContent', {
                             name: name,
                             id: idPlan
                         }) : t('form.modal.createContent', {name: name, id: idPlan})}</p>}
                         cancelButton={t('form.buttons.close')}
                         open={openCreationModal}
                         titleIcon={<CheckCircleRounded color={"success"}/>}
                         onClose={() => {
                             setOpenCreationModal(false);
                             navigate("/salesmarketing/subscriptionlist")
                         }}/>
        </>
    )
}

export default SubscriptionCreateOrUpdatePage;